import { useEffect, useState, useCallback } from "react";
import { ShieldAlert, RotateCcw, Loader2, MessageSquareOff, CheckCircle2, AlertTriangle } from "lucide-react";
import { adminApi, formatApiError } from "./api";
import { toast } from "sonner";
import { PageHeader } from "./shared";

const SEV = {
  high: "bg-[#e4002b]/10 text-[#e4002b]",
  medium: "bg-[#f59e0b]/12 text-[#b45309]",
};

export default function FraudAlerts() {
  const [data, setData] = useState(null);
  const [clearing, setClearing] = useState("");

  const load = useCallback(() => {
    adminApi.get("/fraud-alerts")
      .then((r) => setData(r.data))
      .catch((e) => { toast.error(formatApiError(e.response?.data?.detail) || "Could not load fraud alerts"); setData({ rules: [], alerts: [], flagged_content: [] }); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const clear = async (a) => {
    setClearing(a.key);
    try {
      const { data: res } = await adminApi.post("/fraud-alerts/clear", {
        user_id: a.user_id, rule: a.rule, metric: a.metric, reactivate: a.status === "banned",
      });
      toast.success(res.message || "Alert cleared");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not clear alert");
    } finally {
      setClearing("");
    }
  };

  return (
    <div data-testid="fraud-page">
      <PageHeader title="Fraud Alerts" subtitle="Rule-based detection across bookings, refunds, referrals and blocked content." />
      <div className="space-y-6 p-8">
        {/* rules */}
        <div className="rounded-2xl border border-[#12142b]/8 bg-white p-5 text-sm text-[#565b73]" data-testid="fraud-rules">
          <span className="font-semibold text-[#12142b]">Rule-based detection: </span>
          {(data?.rules || []).map((r, i) => (
            <span key={r.rule}>{r.text} <span className={r.severity === "high" ? "text-[#e4002b]" : "text-[#b45309]"}>({r.severity})</span>{i < data.rules.length - 1 ? " · " : ""}</span>
          ))}
        </div>

        {/* alerts */}
        {data === null ? (
          <p className="py-10 text-center text-sm text-[#565b73]">Scanning for fraud signals…</p>
        ) : data.alerts.length === 0 ? (
          <div className="rounded-2xl border border-[#12142b]/8 bg-white p-10 text-center" data-testid="fraud-empty">
            <CheckCircle2 className="mx-auto h-8 w-8 text-[#16a34a]" />
            <p className="mt-2 text-sm font-semibold text-[#12142b]">No active fraud alerts</p>
            <p className="text-xs text-[#565b73]">No user currently trips the detection rules.</p>
          </div>
        ) : (
          <div className="grid gap-4 lg:grid-cols-2">
            {data.alerts.map((a) => (
              <div key={a.key} className="rounded-2xl border border-[#12142b]/8 bg-white p-5" data-testid={`fraud-alert-${a.key}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-display text-base font-bold text-[#12142b]">{a.name}</p>
                    <p className="text-xs text-[#565b73]">{a.phone || "—"}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase ${SEV[a.severity] || SEV.medium}`}>{a.severity}</span>
                    {a.status === "banned" && (
                      <span className="rounded-full bg-[#e4002b] px-2.5 py-0.5 text-[10px] font-bold uppercase text-white">Banned</span>
                    )}
                  </div>
                </div>
                <p className="mt-2 flex items-center gap-1.5 text-sm font-semibold text-[#b45309]">
                  <AlertTriangle className="h-3.5 w-3.5" /> {a.reason}
                </p>
                <button
                  data-testid={`fraud-clear-${a.key}`}
                  onClick={() => clear(a)}
                  disabled={clearing === a.key}
                  className="mt-3 inline-flex items-center gap-2 rounded-xl bg-[#2340b5] px-4 py-2 text-xs font-bold text-white hover:bg-[#1b3390] disabled:opacity-60"
                >
                  {clearing === a.key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                  Clear / reactivate
                </button>
              </div>
            ))}
          </div>
        )}

        {/* flagged content */}
        <div>
          <h2 className="mb-3 font-display text-lg font-bold text-[#12142b]">Flagged content</h2>
          <div className="rounded-2xl border border-[#12142b]/8 bg-white p-5 text-sm text-[#565b73]">
            The actual blocked texts (chats, community posts, comments) per user — review before suspending or banning.
          </div>
          <div className="mt-3" data-testid="fraud-flagged-list">
            {data && data.flagged_content.length === 0 && (
              <p className="py-6 text-center text-sm font-semibold text-[#565b73]" data-testid="fraud-flagged-empty">
                No flagged content <CheckCircle2 className="inline h-4 w-4 text-[#16a34a]" />
              </p>
            )}
            {(data?.flagged_content || []).map((f) => (
              <div key={f.id || f.text} className="mb-2 rounded-2xl border border-[#12142b]/8 bg-white p-4" data-testid={`flagged-${f.id}`}>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-[#12142b]">{f.user_name}</p>
                  <span className="inline-flex items-center gap-1 rounded-full bg-[#e4002b]/10 px-2 py-0.5 text-[10px] font-bold uppercase text-[#e4002b]">
                    <MessageSquareOff className="h-3 w-3" /> {f.kind}
                  </span>
                </div>
                {f.text && <p className="mt-1 rounded-lg bg-[#f4f6fb] p-2.5 text-xs text-[#565b73]">“{f.text}”</p>}
                {f.created_at && <p className="mt-1 text-[11px] text-[#565b73]/70">{new Date(f.created_at).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</p>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
